import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams, useParams, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { InstructorRoute } from "./components/routes/InstructorRoute";
import { ProtectedRoute, CompleteProfileRoute } from "./components/routes/ProfileRoutes";
import { I18nProvider, useI18n } from "./contexts/I18nContext";
import { FeedFilterProvider } from "./contexts/FeedFilterContext";
import { RealtimeProvider } from "./contexts/RealtimeContext";
import { PWAWrapper } from "./components/PWAWrapper";
import { AuthenticatedPushManager } from "./components/AuthenticatedPushManager";
import { PushNotificationPermissionDialog } from "./components/PushNotificationPermissionDialog";
import { capture as posthogCapture } from "./integrations/analytics/posthog";
import { POSTHOG_PAGEVIEW } from "./integrations/analytics/events";
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
// Social page deprecated — /social now redirects to /sossial via SocialRoute
import ResetPassword from "./pages/ResetPassword";
import ResetPasswordSent from './pages/ResetPasswordSent';
import NotFound from "./pages/NotFound";
import AuthCallback from './pages/AuthCallback';
import AuthError from './pages/AuthError';
import { useEffect } from 'react';
import { getBasename } from '@/utils/url';
import InviteFriend from './pages/InviteFriend';
import Contact from './pages/Contact';
import MyJourney from './pages/MyJourney';
import UserProfile from './pages/UserProfile';
import Search from './pages/Search';
import PostDetail from './pages/PostDetail';
import Calendar from './pages/Calendar';
import { UserPosts } from './pages/UserPosts';
import { UserConnections } from './pages/UserConnections';
import UserAchievements from './pages/UserAchievements';
import { UserRecognitions } from './pages/UserRecognitions';
import { UserStatistics } from './pages/UserStatistics';
import { InstructorSlots } from './pages/InstructorSlots';
import Home from './pages/Home';
import Sossial from './pages/Sossial';
import CreatePost from './pages/CreatePost';
import ConnectionDiscover from './pages/ConnectionDiscover';
import Onboarding from './pages/Onboarding';
import CompleteProfile from './pages/CompleteProfile';
import TermsOfUse from './pages/TermsOfUse';
import PrivacyPolicies from './pages/PrivacyPolicies';
import IndependentContractorAgreement from './pages/IndependentContractorAgreement';
import Messages from './pages/Messages';
import VideoReview from './pages/VideoReview';
import VideoReviewDetail from './pages/VideoReviewDetail';
import QuickActions from './features/quick-actions';
import BrowseInstructors from './features/roll-review/pages/BrowseInstructors';
import RequestRollReview from './features/roll-review/pages/RequestRollReview';
import MyReviews from './features/roll-review/pages/MyReviews';
import MyPurchasesPage from './features/roll-review/pages/MyPurchases';
import ManageReviews from './features/roll-review/pages/ManageReviews';
import CancelRequest from './features/roll-review/pages/CancelRequest';
import Conversation from './pages/Conversation';
import NewConversation from './pages/NewConversation';
import Settings from './pages/Settings';
import EditProfile from './pages/settings/EditProfile';
import SettingsNotifications from './pages/settings/Notifications';
import InstructorTools from './pages/settings/InstructorTools';
import InstructorToolsPricing  from './pages/settings/instructor-tools/Pricing';
import InstructorToolsDuration from './pages/settings/instructor-tools/Duration';
import InstructorToolsSchedule from './pages/settings/instructor-tools/Schedule';
import InstructorToolsEarnings from './pages/settings/instructor-tools/Earnings';
import WalletPage from './features/wallet/pages/WalletPage';
import TransactionDetailPage from './features/wallet/pages/TransactionDetailPage';
import PaymentMethodsPage from './features/subscription/pages/PaymentMethodsPage';
import SubscriptionPlanPage from './features/subscription/pages/SubscriptionPlanPage';
import SubscriptionCheckoutPlaceholderPage from './features/subscription/pages/SubscriptionCheckoutPlaceholderPage';
import SubscriptionSuccessPage from './features/subscription/pages/SubscriptionSuccessPage';
import SubscriptionManagePage from './features/subscription/pages/SubscriptionManagePage';
import CancelSubscriptionPage from './features/subscription/pages/CancelSubscriptionPage';
import BillingHistoryPage from './features/subscription/pages/BillingHistoryPage';
import { PastDueBanner } from './features/subscription/components/PastDueBanner';
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const LoadingScreen = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#160C0C] text-white">
      <div className="text-lg font-medium">Loading...</div>
    </div>
  );
};

const InstructorShortLinkRedirect = () => {
  const { username } = useParams<{ username: string }>();
  return <Navigate to={`/instructor-slots/${username ?? ''}`} replace />;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const pendingEmailVerification =
    typeof sessionStorage !== 'undefined' &&
    sessionStorage.getItem('auth_pending_email_verification') === '1';
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (user && !pendingEmailVerification) {
    return <Navigate to="/home" replace />;
  }
  
  return <>{children}</>;
};

const PublicAccessRoute = ({ children }: { children: React.ReactNode }) => {
  const { loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  return <>{children}</>;
};

const ResetPasswordRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const onHashChange = () => {
      window.location.reload();
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  return <>{children}</>;
};

const SocialRoute = () => <Navigate to="/sossial" replace />;

const UserPostsRoute = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user } = useAuth();
  const isOwnProfile = user?.id === userId;
  
  return <UserPosts userId={userId || ''} isOwnProfile={isOwnProfile} />;
};

const UserConnectionsRoute = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user } = useAuth();
  const isOwnProfile = user?.id === userId;
  
  return <UserConnections userId={userId || ''} isOwnProfile={isOwnProfile} />;
};

const UserAchievementsRoute = () => {
  const { userId } = useParams<{ userId: string }>();
  
  return <UserAchievements />;
};

const UserRecognitionsRoute = () => {
  return <UserRecognitions />;
};

const UserStatisticsRoute = () => {
  return <UserStatistics />;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const PageviewTracker = () => {
  // Fires on real route changes (pathname). Search-string churn (filter
  // toggles, tab params) is intentionally excluded so it does not inflate
  // pageview counts in PostHog. Hash-only navigations also do not fire,
  // which is fine for BrowserRouter.
  const { pathname, search } = useLocation();
  useEffect(() => {
    posthogCapture(POSTHOG_PAGEVIEW, { $pathname: pathname, $search: search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  return null;
};

const AppRoutes = () => {
  return (
    <>
      <ScrollToTop />
      <PageviewTracker />
      {/* Phase 8 / SOS-335: sticky banner shown across all authenticated
          screens while subscription is past_due / suspended. It self-hides
          on /subscription and /settings/subscription where the page already
          shows an inline banner. */}
      <PastDueBanner />
          <Routes>
            <Route path="/" element={
              <PublicRoute>
                <Index />
              </PublicRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/home" element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            } />
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            } />
            <Route path="/complete-profile" element={
              <CompleteProfileRoute>
                <CompleteProfile />
              </CompleteProfileRoute>
            } />
            <Route path="/social" element={
              <SocialRoute />
            } />
            <Route path="/sossial" element={
              <FeedFilterProvider>
                <Sossial />
              </FeedFilterProvider>
            } />
            <Route path="/sossial/create" element={
              <ProtectedRoute>
                <CreatePost />
              </ProtectedRoute>
            } />
            <Route path="/sossial/connections" element={
              <ProtectedRoute>
                <ConnectionDiscover />
              </ProtectedRoute>
            } />
            <Route path="/profile/:username" element={<UserProfile />} />
            <Route path="/profile" element={
              <ProtectedRoute>
                <MyJourney />
              </ProtectedRoute>
            } />
            <Route path="/i/:username" element={<InstructorShortLinkRedirect />} />
            <Route path="/instructor-slots/:instructorUsername" element={<InstructorSlots />} />
            <Route path="/reset-password" element={
              <ResetPasswordRoute>
                <ResetPassword />
              </ResetPasswordRoute>
            } />
            <Route path="/reset-password-sent" element={<ResetPasswordSent />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/error" element={<AuthError />} />
            <Route path="/terms-of-use" element={
              <PublicAccessRoute>
                <TermsOfUse />
              </PublicAccessRoute>
            } />
            <Route path="/privacy-policies" element={
              <PublicAccessRoute>
                <PrivacyPolicies />
              </PublicAccessRoute>
            } />
            {/* Public like the other two legal documents: an instructor must be
                able to read what they are being asked to accept, and a signed-out
                or mid-onboarding visitor must not hit a redirect (DOC-01 AC-10). */}
            <Route path="/independent-contractor-agreement" element={
              <PublicAccessRoute>
                <IndependentContractorAgreement />
              </PublicAccessRoute>
            } />
            <Route path="/invite-friend" element={
              <ProtectedRoute>
                <InviteFriend />
              </ProtectedRoute>
            } />
            <Route path="/contact" element={
              <PublicAccessRoute>
                <Contact />
              </PublicAccessRoute>
            } />
            <Route path="/search" element={
              <ProtectedRoute>
                <Search />
              </ProtectedRoute>
            } />
            <Route path="/post/:postId" element={
              <ProtectedRoute>
                <PostDetail />
              </ProtectedRoute>
            } />
            <Route path="/calendar" element={
              <ProtectedRoute>
                <Calendar />
              </ProtectedRoute>
            } />
            <Route path="/user/posts/:userId" element={
              <ProtectedRoute>
                <UserPostsRoute />
              </ProtectedRoute>
            } />
            <Route path="/user/connections/:userId" element={
              <ProtectedRoute>
                <UserConnectionsRoute />
              </ProtectedRoute>
            } />
            <Route path="/user/achievements/:userId" element={
              <ProtectedRoute>
                <UserAchievementsRoute />
              </ProtectedRoute>
            } />
            <Route path="/user/recognitions/:userId" element={
              <ProtectedRoute>
                <UserRecognitionsRoute />
              </ProtectedRoute>
            } />
            <Route path="/user/statistics/:userId" element={
              <ProtectedRoute>
                <UserStatisticsRoute />
              </ProtectedRoute>
            } />
            <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
            <Route path="/messages/new" element={<ProtectedRoute><NewConversation /></ProtectedRoute>} />
            <Route path="/messages/:id" element={<ProtectedRoute><Conversation /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/settings/edit-profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
            <Route path="/settings/notifications" element={<ProtectedRoute><SettingsNotifications /></ProtectedRoute>} />
            <Route path="/settings/instructor-tools"          element={<InstructorRoute><InstructorTools /></InstructorRoute>} />
            <Route path="/settings/instructor-tools/pricing"  element={<InstructorRoute><InstructorToolsPricing /></InstructorRoute>} />
            <Route path="/settings/instructor-tools/duration" element={<InstructorRoute><InstructorToolsDuration /></InstructorRoute>} />
            <Route path="/settings/instructor-tools/schedule" element={<InstructorRoute><InstructorToolsSchedule /></InstructorRoute>} />
            <Route path="/settings/instructor-tools/earnings" element={<InstructorRoute><InstructorToolsEarnings /></InstructorRoute>} />
            <Route path="/video-review" element={<ProtectedRoute><VideoReview /></ProtectedRoute>} />
            <Route path="/video-review/:videoId" element={<ProtectedRoute><VideoReviewDetail /></ProtectedRoute>} />
            <Route path="/quick-actions" element={<ProtectedRoute><QuickActions /></ProtectedRoute>} />
            <Route path="/browse-instructors" element={<ProtectedRoute><BrowseInstructors /></ProtectedRoute>} />
            <Route path="/request-roll-review/:instructorId" element={<ProtectedRoute><RequestRollReview /></ProtectedRoute>} />
            <Route path="/my-reviews" element={<ProtectedRoute><MyReviews /></ProtectedRoute>} />
            <Route path="/my-purchases" element={<ProtectedRoute><MyPurchasesPage /></ProtectedRoute>} />
            <Route path="/my-roll-reviews" element={<InstructorRoute><ManageReviews /></InstructorRoute>} />
            <Route path="/cancel-review/:videoId" element={<ProtectedRoute><CancelRequest /></ProtectedRoute>} />
            <Route path="/wallet" element={<InstructorRoute><WalletPage /></InstructorRoute>} />
            {/* Wallet onboarding lives at /settings/instructor-tools/earnings, which is
                where every entry point and the Stripe return_url already send the
                instructor. /wallet/setup was the address of a second copy of the wizard
                that no route ever pointed at; it is kept here as a redirect so the
                address resolves if it ever reaches an email or a push. */}
            <Route path="/wallet/setup" element={<Navigate to="/settings/instructor-tools/earnings" replace />} />
            <Route path="/wallet/transaction/:transactionId" element={<InstructorRoute><TransactionDetailPage /></InstructorRoute>} />
            <Route path="/settings/payment-methods" element={<ProtectedRoute><PaymentMethodsPage /></ProtectedRoute>} />
            <Route path="/subscription" element={<ProtectedRoute><SubscriptionPlanPage /></ProtectedRoute>} />
            <Route path="/subscription/checkout" element={<ProtectedRoute><SubscriptionCheckoutPlaceholderPage /></ProtectedRoute>} />
            <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccessPage /></ProtectedRoute>} />
            <Route path="/settings/subscription" element={<ProtectedRoute><SubscriptionManagePage /></ProtectedRoute>} />
            <Route path="/settings/subscription/cancel" element={<ProtectedRoute><CancelSubscriptionPage /></ProtectedRoute>} />
            <Route path="/settings/subscription/billing" element={<ProtectedRoute><BillingHistoryPage /></ProtectedRoute>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
    </>
  );
};

const App = () => {
  const basename = getBasename();

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'navigate') {
          const url = event.data.url;
          if (url) {
            let fullUrl: string;
            let cleanUrl = url.trim();
            
            cleanUrl = cleanUrl.replace(/https?:\/\/localhost:\d+/gi, '');
            cleanUrl = cleanUrl.replace(/localhost:\d+/gi, '');
            cleanUrl = cleanUrl.replace(/https?:\/\/127\.0\.0\.1:\d+/gi, '');
            cleanUrl = cleanUrl.replace(/127\.0\.0\.1:\d+/gi, '');
            
            const firstSlashIndex = cleanUrl.indexOf('/');
            if (firstSlashIndex >= 0) {
              cleanUrl = cleanUrl.substring(firstSlashIndex);
            } else {
              cleanUrl = '/';
            }
            
            if (!cleanUrl.startsWith('/')) {
              cleanUrl = '/' + cleanUrl;
            }
            
            const cleanBasename = basename === '/' ? '' : basename.replace(/\/+$/, '');
            const finalPath = cleanUrl.startsWith('/') ? cleanUrl : '/' + cleanUrl;
            fullUrl = `${window.location.origin}${cleanBasename}${finalPath}`;
            
            const originLength = window.location.origin.length;
            const pathPart = fullUrl.substring(originLength);
            const cleanedPath = pathPart.replace(/\/+/g, '/');
            fullUrl = window.location.origin + cleanedPath;
            
            if (fullUrl.includes('localhost:8080/localhost:8080')) {
              fullUrl = fullUrl.replace(/localhost:8080\/localhost:8080/g, 'localhost:8080');
            }
            
            if (fullUrl.includes('localhost:8080/localhost:8080') || (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://'))) {
              fullUrl = `${window.location.origin}${cleanBasename}/`;
            }
            
            window.location.href = fullUrl;
          }
        }
      });
    }
  }, [basename]);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Router basename={basename}>
          <I18nProvider>
            <AuthProvider>
              <RealtimeProvider>
                <FeedFilterProvider>
                  <PWAWrapper>
                    <AuthenticatedPushManager />
                    <PushNotificationPermissionDialog />
                    <Toaster />
                    <Sonner />
                    <AppRoutes />
                  </PWAWrapper>
                </FeedFilterProvider>
              </RealtimeProvider>
            </AuthProvider>
          </I18nProvider>
        </Router>
      </TooltipProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default App;
